import React, { useState } from "react";
import {
  Box,
  Button,
  HStack,
  Icon,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { FaArrowLeft, FaCog, FaHistory, FaSave, FaTimes } from "react-icons/fa";
import JobCreationComponent from "./JobCreationComponent";
import { ScenarioAnalysis } from "build-ts/analyst";
import { Header } from "build-ts/metadata";
import { IoIosInfinite } from "react-icons/io";
import showToast from "hooks/useCustomToast";
import { runJobAsync } from "services/ScheduleServices";
import { MetadataTypeEnum, metadataTypeEnumToJSON } from "build-ts/enums";
import JobRunsViewer from "./JobRunsViewer";

interface JobViewComponentProps {
  onBack: () => void;
  scenarios: ScenarioAnalysis[] | undefined;
  isCreatingNew?: boolean;
  selectedJob?: Header;
  isJobsTabActive?: boolean;
}

const JobViewComponent: React.FC<JobViewComponentProps> = ({
  onBack,
  scenarios,
  isCreatingNew = true,
  selectedJob,
  isJobsTabActive = false,
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [triggerSave, setTriggerSave] = useState<boolean>(false);
  const [hasFormChanges, setHasFormChanges] = useState<boolean>(false);

  const handleSave = () => {
    setTriggerSave(true);
  };

  // Handle run job
  const handleRunJob = async () => {
    if (!selectedJob || !selectedJob.id) return;
    await runJobAsync(selectedJob?.id, metadataTypeEnumToJSON(MetadataTypeEnum.ANALYST));
    showToast(
      "Job is running",
      "The scheduled job has been triggered and is now running.",
      "success"
    );
  };

  const handleCancel = () => {
    onBack();
  };

  return (
    <Box height="100%" bg="white" display="flex" flexDirection="column">
      {/* Tabs */}
      <Box flex="1" overflowY="hidden">
        <Tabs
          colorScheme="purple"
          isFitted
          variant="line"
          size="md"
          height="100%"
          defaultIndex={0}
          index={activeTab}
          onChange={setActiveTab}
        >
          <HStack p={2}>
            {/* Left Side: Back Button */}
            <HStack spacing={4} justify="flex-start">
              {/* Back Button */}
              <Button
                leftIcon={<Icon as={FaArrowLeft} />}
                variant="ghost"
                size="sm"
                onClick={onBack}
                color="gray.700"
                _hover={{ bg: "gray.100" }}
                flexShrink={0}
                iconSpacing={0}
              />
            </HStack>
            {/* Right Side: Tabs */}
            <TabList flex="1">
              <Tab _hover={{ bg: "purple.50" }}>
                <HStack spacing={2}>
                  <Icon as={FaCog} boxSize={5} />
                  <Text mb={0} fontSize="md" fontWeight="700">Configure</Text>
                </HStack>
              </Tab>
              <Tab _hover={{ bg: "purple.50" }}>
                <HStack spacing={2}>
                  <Icon as={FaHistory} boxSize={5} />
                  <Text mb={0} fontSize="md" fontWeight="700">Past Runs</Text>
                </HStack>
              </Tab>
            </TabList>
          </HStack>

          <TabPanels height="calc(100% - 48px)">
            <TabPanel height="100%" p={0}>
              <JobCreationComponent
                selectedJob={selectedJob}
                scenarios={scenarios}
                isCreatingNew={isCreatingNew}
                triggerSave={triggerSave}
                setTriggerSave={setTriggerSave}
                setIsSaving={setIsSaving}
                onSaveSuccess={onBack}
                setHasFormChanges={setHasFormChanges}
              />
            </TabPanel>
            <TabPanel height="100%" p={4}>
              <JobRunsViewer selectedJob={selectedJob} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* Overlay Buttons - Only show when Jobs tab is active */}
      {isJobsTabActive && (
        <Box
          position="fixed"
          bottom="20px"
          right="20px"
          bg="white"
          border="1px"
          borderColor="gray.200"
          borderRadius="lg"
          boxShadow="lg"
          p={3}
          zIndex={1000}
        >
          <HStack spacing={3} justify="space-around">
            {/* Run Button */}
            {!isCreatingNew && selectedJob?.id && (
              <Button
                colorScheme="purple"
                size="md"
                onClick={handleRunJob}
                leftIcon={<Icon as={IoIosInfinite} boxSize={4} />}
              >
                Run
              </Button>
            )}

            {/* Save/Update and Cancel buttons */}
            {(hasFormChanges || isCreatingNew) && (
              <>
                <Button
                  colorScheme="green"
                  size="md"
                  onClick={handleSave}
                  isLoading={isSaving}
                  loadingText={isCreatingNew ? "Saving..." : "Updating..."}
                  leftIcon={<Icon as={FaSave} boxSize={3} />}
                >
                  {isCreatingNew ? "Save" : "Update"}
                </Button>
                <Button
                  colorScheme="red"
                  variant="outline"
                  size="md"
                  onClick={handleCancel}
                  isDisabled={isSaving}
                  leftIcon={<Icon as={FaTimes} boxSize={3} />}
                >
                  {isCreatingNew ? "Cancel" : "Discard"}
                </Button>
              </>
            )}
          </HStack>
        </Box>
      )}
    </Box>
  );
};

export default JobViewComponent;
